import { useParams, Link } from "wouter";
import { useSEO } from "@/hooks/use-seo";
import { PublicHeader } from "@/components/public-header";
import { PublicFooter } from "@/components/public-footer";
import { Button } from "@/components/ui/button";
import { getGroupBySlug, buildGroupJsonLd } from "@/data/feature-catalog";
import { marketingFeatureCatalog as featureCatalog, visibleSubFeatures } from "@/data/marketing-catalog";
import { StartFreeButton, TourButton } from "@/components/marketing/cta";
import { groupSEO } from "@/data/feature-seo";
import { useState, useEffect, useRef } from "react";
import {
  ArrowRight,
  ChevronRight,
  MessageSquare,
  Building2,
  Rocket,
  Users,
  Wrench,
  Briefcase,
  DollarSign,
  Plug,
  Inbox,
  Phone,
  MessageCircle,
  Bell,
  Home,
  Map,
  Download,
  Building,
  Calculator,
  TrendingUp,
  Search,
  ClipboardList,
  KeyRound,
  UserCheck,
  Landmark,
  ShoppingBag,
  Hammer,
  Sofa,
  Package,
  FileText,
  Lock,
  Thermometer,
  Camera,
  Store,
  CreditCard,
  Settings,
  BarChart3,
  Receipt,
  RefreshCw,
  AlertCircle,
  Mail,
  Globe,
  ExternalLink,
  Tag,
  Wifi,
  Star,
  Car,
  ClipboardCheck,
  Trash2,
  User,
  Sparkles,
  type LucideIcon,
} from "lucide-react";

const ICON_MAP: Record<string, LucideIcon> = {
  MessageSquare,
  Building2,
  Rocket,
  Users,
  Wrench,
  Briefcase,
  DollarSign,
  Plug,
  Inbox,
  Phone,
  MessageCircle,
  Bell,
  Home,
  Map,
  Download,
  Building,
  Calculator,
  TrendingUp,
  Search,
  ClipboardList,
  KeyRound,
  UserCheck,
  Landmark,
  ShoppingBag,
  Hammer,
  Sofa,
  Package,
  FileText,
  Lock,
  Thermometer,
  Camera,
  Store,
  CreditCard,
  Settings,
  BarChart3,
  Receipt,
  RefreshCw,
  AlertCircle,
  Mail,
  Globe,
  ExternalLink,
  Tag,
  Wifi,
  Star,
  Car,
  ClipboardCheck,
  Trash2,
  User,
  Sparkles,
};

function useScrollAnimation() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return { ref, visible };
}

function AnimatedSection({
  children,
  className = "",
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const { ref, visible } = useScrollAnimation();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      } ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

export default function FeaturesGroupPage() {
  const params = useParams<{ groupSlug: string }>();
  const groupSlug = params.groupSlug;
  const catalogGroup = getGroupBySlug(groupSlug);
  // Cut and hidden features (F8) are not listed.
  const group = catalogGroup ? { ...catalogGroup, subFeatures: visibleSubFeatures(catalogGroup) } : undefined;

  const seo = group ? groupSEO(group) : null;
  useSEO({
    tabTitle: seo ? seo.tabTitle : "Feature Group",
    title: seo ? seo.title : "Feature Group — PadPulse",
    description: seo
      ? seo.description
      : "Explore PadPulse co-living management platform features.",
    path: group ? `/features/${group.slug}` : "/features",
    jsonLd: group ? buildGroupJsonLd(group) : undefined,
  });

  if (!group) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <PublicHeader />
        <main className="flex-grow flex items-center justify-center pt-20">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Feature group not found</h1>
            <p className="text-gray-500 mb-6">The feature group you're looking for doesn't exist.</p>
            <Link href="/features">
              <Button>Browse all features</Button>
            </Link>
          </div>
        </main>
        <PublicFooter />
      </div>
    );
  }

  const GroupIcon = ICON_MAP[group.icon] ?? Building2;
  const otherGroups = featureCatalog.filter((g) => g.slug !== group.slug);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <PublicHeader />

      {/* Hero */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#0a1628] via-[#0d2447] to-brand pt-28 md:pt-36 pb-20 md:pb-28">
        <div className="absolute inset-0 pointer-events-none">
          <div className={`absolute top-20 left-10 w-80 h-80 rounded-full blur-3xl opacity-20`}
            style={{ background: group.gradientFrom }} />
          <div className={`absolute bottom-10 right-10 w-96 h-96 rounded-full blur-3xl opacity-10`}
            style={{ background: group.gradientTo }} />
        </div>

        <div className="relative container mx-auto px-6">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-white/40 mb-8" aria-label="Breadcrumb">
            <Link href="/" className="hover:text-white/70 transition-colors">Home</Link>
            <ChevronRight size={12} />
            <Link href="/features" className="hover:text-white/70 transition-colors">Features</Link>
            <ChevronRight size={12} />
            <span className={group.textColor}>{group.name}</span>
          </nav>

          <div className="max-w-3xl">
            <div className={`inline-flex items-center gap-2 ${group.bgColor} border ${group.borderColor} rounded-full px-4 py-1.5 text-sm font-medium ${group.textColor} mb-6`}>
              <GroupIcon size={14} />
              {group.name}
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-[1.1] tracking-tight"
              data-testid="text-group-hero-heading">
              {group.tagline}
            </h1>

            <p className="text-lg md:text-xl text-blue-100 mb-8 max-w-2xl leading-relaxed">
              {group.description}
            </p>

            <div className="flex flex-wrap gap-3">
              <StartFreeButton ctaId="group-hero-start-free" location="features_group_hero" className="px-8" />
              <TourButton ctaId="group-hero-tour" location="features_group_hero" className="px-8" />
            </div>
          </div>
        </div>
      </div>

      {/* Sub-feature cards with anchored sections */}
      <main id="main-content" className="flex-grow bg-gray-950 py-16 md:py-24">
        <div className="container mx-auto px-6">
          <AnimatedSection>
            <div className="text-center mb-14">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                Everything in {group.name}
              </h2>
              <p className="text-gray-400 max-w-xl mx-auto">
                {group.subFeatures.length} features — click any card to explore the full details.
              </p>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {group.subFeatures.map((sf, i) => {
              const SfIcon = ICON_MAP[sf.icon] ?? Building2;
              return (
                <AnimatedSection key={sf.slug} delay={i * 60}>
                  <div id={sf.slug} className="scroll-mt-24">
                    <Link href={`/features/${group.slug}/${sf.slug}`}>
                      <div
                        className={`group h-full bg-gray-900 border ${group.borderColor} hover:border-white/20 rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-black/40 cursor-pointer`}
                        data-testid={`card-subfeature-${sf.slug}`}
                      >
                        <div className={`inline-flex items-center justify-center w-10 h-10 rounded-xl ${group.bgColor} border ${group.borderColor} mb-4`}>
                          <SfIcon size={18} className={group.textColor} />
                        </div>

                        <h3 className="text-white font-semibold text-lg mb-2 group-hover:text-brand-accent transition-colors">
                          {sf.name}
                        </h3>
                        <p className="text-gray-400 text-sm leading-relaxed mb-4">
                          {sf.tagline}
                        </p>

                        <ul className="space-y-1.5 mb-5">
                          {sf.benefits.slice(0, 3).map((b) => (
                            <li key={b} className="flex items-start gap-2 text-xs text-gray-400">
                              <span className={`mt-0.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${group.bgColor} ring-1 ring-current ${group.textColor}`} />
                              {b}
                            </li>
                          ))}
                        </ul>

                        <div className={`flex items-center gap-1 text-xs font-medium ${group.textColor} group-hover:gap-2 transition-all`}>
                          Learn more <ArrowRight size={12} />
                        </div>
                      </div>
                    </Link>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
          {group.footnote && (
            <p
              className="text-sm text-white/40 max-w-6xl mx-auto mt-8"
              data-testid="text-group-footnote"
            >
              {group.footnote}
            </p>
          )}
        </div>
      </main>

      {/* Explore other groups */}
      <section className="bg-gray-900 py-14 md:py-20">
        <div className="container mx-auto px-6">
          <AnimatedSection>
            <h2 className="text-xl font-bold text-white mb-8 text-center">Explore other feature groups</h2>
          </AnimatedSection>
          <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
            {otherGroups.map((g) => {
              const OtherIcon = ICON_MAP[g.icon] ?? Building2;
              return (
                <Link
                  key={g.slug}
                  href={`/features/${g.slug}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full ${g.bgColor} border ${g.borderColor} ${g.textColor} text-sm font-medium hover:opacity-80 transition-opacity`}
                  data-testid={`link-other-group-${g.slug}`}
                >
                  <OtherIcon size={14} />
                  {g.name}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-br from-[#0a1628] to-[#0d2447] py-16 md:py-20">
        <div className="container mx-auto px-6 text-center">
          <AnimatedSection>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to put {group.name} to work?
            </h2>
            <p className="text-blue-200 mb-8 max-w-xl mx-auto">
              Sign up for access to PadPulse's full {group.name.toLowerCase()} feature suite.
            </p>
            <StartFreeButton ctaId="group-footer-start-free" location="features_group_footer" className="px-10" />
          </AnimatedSection>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
